// Client-side run orchestration (plan §7): for each selected model, run every dataset
// item through the candidate AND baseline prompt sets, score both against the golden,
// and aggregate into per-model results. Deterministic given a deterministic executor.

import type { DatasetItem } from 'app/components/ops-datasets/mockData'
import { aggregateScores, scoreExtraction, SCORED_STAGES, type ExtractionScore, type Stage } from './scoring'
import { costForUsage } from './pricing'
import type { Executor } from './executor'
import { EXECUTION_STAGES, type ItemResult, type Model, type ModelResult, type Run, type RunConfig } from './types'

const EPS = 1e-9

/** Items in flight at once. Each item runs its candidate and baseline side by side, so a
 *  real-model run makes up to twice this many concurrent calls. */
const ITEM_CONCURRENCY = 4

function stageAccuracyMap(agg: ReturnType<typeof aggregateScores>): Record<Stage, number> {
  return Object.fromEntries(SCORED_STAGES.map((stage) => [stage, agg.stages[stage].accuracy])) as Record<Stage, number>
}

function latencyFor(itemId: string, model: Model): number {
  let h = 0
  const key = `${itemId}:${model}`
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return 700 + (h % 1900)
}

async function runModel(
  model: Model,
  config: RunConfig,
  items: DatasetItem[],
  executor: Executor,
  onItemDone: () => void,
): Promise<ModelResult> {
  const execution = config.execution ?? `simulated`
  const scope = EXECUTION_STAGES[execution]
  const itemResults: ItemResult[] = new Array(items.length)
  const candidateScores: ExtractionScore[] = new Array(items.length)
  const baselineScores: ExtractionScore[] = new Array(items.length)

  // A small worker pool. Results are written by index, so their order matches the
  // dataset no matter which call finishes first.
  let nextIndex = 0
  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++
      const item = items[index]
      // Each side is timed on its own, so a one-call pipeline shows its speed against five.
      const timed = async (promptSet: typeof config.candidate, side: string) => {
        const started = Date.now()
        const output = await executor({ item, promptSet, model, temperature: config.temperature })
        const latencyMs = execution === `simulated` ? latencyFor(`${item.id}:${side}`, model) : Date.now() - started
        return { ...output, latencyMs }
      }
      const [candidate, baseline] = await Promise.all([
        timed(config.candidate, `candidate`),
        timed(config.baseline, `baseline`),
      ])

      const candidateScore = scoreExtraction(candidate.extraction, item.golden, scope)
      const baselineScore = scoreExtraction(baseline.extraction, item.golden, scope)
      candidateScores[index] = candidateScore
      baselineScores[index] = baselineScore

      itemResults[index] = {
        itemId: item.id,
        callerName: item.callerName,
        model,
        candidateScore,
        baselineScore,
        tokens: candidate.usage,
        cost: costForUsage(model, candidate.usage),
        latencyMs: candidate.latencyMs,
        baseline: {
          tokens: baseline.usage,
          cost: costForUsage(model, baseline.usage),
          latencyMs: baseline.latencyMs,
        },
        candidateText: { summary: candidate.extraction.summary, actionItems: candidate.extraction.actionItems },
        baselineText: { summary: baseline.extraction.summary, actionItems: baseline.extraction.actionItems },
        improved: candidateScore.overall > baselineScore.overall + EPS,
        regressed: candidateScore.overall < baselineScore.overall - EPS,
      }
      onItemDone()
    }
  }
  await Promise.all(Array.from({ length: Math.min(ITEM_CONCURRENCY, items.length) }, worker))

  const candidateAgg = aggregateScores(candidateScores)
  const baselineAgg = aggregateScores(baselineScores)

  const fieldAccuracy = Object.fromEntries(
    Object.entries(candidateAgg.fields).map(([key, value]) => [
      key,
      { stage: value.stage, field: value.field, accuracy: value.accuracy, matched: value.matched, total: value.total },
    ]),
  )

  const totalTokens = itemResults.reduce((sum, r) => sum + r.tokens.promptTokens + r.tokens.completionTokens, 0)
  const cost = itemResults.reduce((sum, r) => sum + r.cost, 0)

  return {
    model,
    accuracy: candidateAgg.overall,
    baselineAccuracy: baselineAgg.overall,
    delta: candidateAgg.overall - baselineAgg.overall,
    stageAccuracy: stageAccuracyMap(candidateAgg),
    baselineStageAccuracy: stageAccuracyMap(baselineAgg),
    fieldAccuracy,
    improvedCount: itemResults.filter((r) => r.improved).length,
    regressedCount: itemResults.filter((r) => r.regressed).length,
    unchangedCount: itemResults.filter((r) => !r.improved && !r.regressed).length,
    totalTokens,
    cost,
    baselineCost: itemResults.reduce((sum, r) => sum + r.baseline.cost, 0),
    items: itemResults,
  }
}

export type RunProgressCount = { done: number; total: number; model: Model }

export async function runEval(params: {
  config: RunConfig
  datasetName: string
  items: DatasetItem[]
  executor: Executor
  startedBy?: string
  /** Called as each call finishes (both sides), counting across every model. */
  onProgress?: (progress: RunProgressCount) => void
}): Promise<Run> {
  const { config, datasetName, items, executor, startedBy = `You`, onProgress } = params
  const startedAt = new Date().toISOString()
  const modelResults: ModelResult[] = []
  const total = items.length * config.models.length
  let done = 0
  for (const model of config.models) {
    const onItemDone = () => onProgress?.({ done: ++done, total, model })
    modelResults.push(await runModel(model, config, items, executor, onItemDone))
  }
  return {
    id: `run_${Date.now()}`,
    datasetId: config.datasetId,
    datasetName,
    candidate: config.candidate,
    baseline: config.baseline,
    models: config.models,
    temperature: config.temperature,
    startedAt,
    finishedAt: new Date().toISOString(),
    startedBy,
    status: `complete`,
    execution: config.execution ?? `simulated`,
    stages: EXECUTION_STAGES[config.execution ?? `simulated`],
    modelResults,
  }
}
